(() => {
  if (customElements.get('quantity-offer-controls')) return;
  class QuantityOfferControls extends HTMLElement {
    connectedCallback() {
      this.lifecycle = new AbortController();
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.delay);
      this.lifecycle?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.restoreCheckout();
      if (this.count > 1 && this.form?.elements.quantity) this.form.elements.quantity.value = 1;
    }

    initialize() {
      this.product = JSON.parse(this.querySelector('[data-offer-product]').textContent);
      this.root = this.closest('[data-codex-quantity-offers]');
      this.form = document.getElementById(this.dataset.formId) || this.root.parentElement.querySelector('form[data-product-form]');
      if (!this.form || !this.product.variants.length) return;
      this.count = 1;
      this.sequence = 0;
      this.ids = Array(3).fill(Number(this.form.elements.id.value) || this.product.selected);
      this.mainId = this.ids[0];
      this.cards = [...this.root.querySelectorAll('[data-codex-quantity-card]')];
      this.items = this.querySelector('[data-offer-items]');
      this.status = this.querySelector('[data-offer-total]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD'
      });
      const options = {signal: this.lifecycle.signal};
      this.cards.forEach((card, i) => card.addEventListener('click', () => {
        this.syncMain();
        this.count = i + 1;
        if (this.form.elements.quantity) this.form.elements.quantity.value = this.count;
        this.render();
        this.schedule();
      }, options));
      this.form.addEventListener('change', () => this.syncMain(), options);
      this.observer = new MutationObserver(() => this.syncMain());
      this.observer.observe(this.form.elements.id, {attributes: true, attributeFilter: ['value']});
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncMain());
      }
      this.form.addEventListener('submit', event => {
        this.syncMain();
        if (this.count > 1 && this.form.elements.selling_plan?.value) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.status.textContent = 'Select 1 item for a subscription purchase.';
        }
      }, {...options, capture: true});
      // Preserve the theme's existing validation, cart drawer and error handling.
      this.form.addEventListener('formdata', event => this.prepareItems(event.formData), options);
      this.render();
      this.schedule();
    }

    variant(id) { return this.product.variants.find(v => v.id === Number(id)); }

    syncMain() {
      const id = Number(this.form.elements.id.value);
      if (id && id !== this.mainId && this.variant(id)) {
        this.mainId = id;
        this.ids = this.ids.map(() => id);
        this.render();
        this.schedule();
      }
    }

    restoreCheckout() {
      this.checkoutState?.forEach(([element, hidden]) => {element.hidden = hidden;});
      this.checkoutState = null;
    }

    render() {
      this.cards.forEach((card, index) => {
        card.classList.toggle('is-active', index + 1 === this.count);
        card.setAttribute('aria-pressed', String(index + 1 === this.count));
      });
      this.items.replaceChildren();
      if (this.count > 1 && this.product.variants.length > 1) {
        this.ids.slice(0, this.count).forEach((id, itemIndex) => {
          const row = document.createElement('div');
          row.className = 'quantity-offer-item';
          const number = document.createElement('span');
          number.textContent = `#${itemIndex + 1}`;
          row.append(number);
          const fields = document.createElement('div');
          fields.className = 'quantity-offer-options';
          const current = this.variant(id);
          this.product.options.forEach((name, optionIndex) => {
            const label = document.createElement('label');
            label.append(document.createTextNode(name));
            const select = document.createElement('select');
            select.setAttribute('aria-label', `Item ${itemIndex + 1}: ${name}`);
            [...new Set(this.product.variants.map(v => v.options[optionIndex]))].forEach(value => {
              const option = new Option(value, value);
              // Later options depend on earlier choices; changing color picks a valid style.
              option.disabled = !this.product.variants.some(v => v.available && v.options[optionIndex] === value && v.options.slice(0, optionIndex).every((x, i) => x === current.options[i]));
              select.add(option);
            });
            select.value = current.options[optionIndex];
            select.addEventListener('change', () => {
              const matches = this.product.variants.filter(v => v.available && v.options[optionIndex] === select.value && v.options.slice(0, optionIndex).every((x, i) => x === current.options[i]));
              const next = matches.find(v => v.options.slice(optionIndex + 1).every((x, i) => x === current.options[optionIndex + 1 + i])) || matches[0];
              if (!next) return;
              this.ids[itemIndex] = next.id;
              this.render();
              this.items.children[itemIndex].querySelectorAll('select')[optionIndex].focus({preventScroll: true});
              this.schedule();
            });
            label.append(select);
            fields.append(label);
          });
          row.append(fields);
          this.items.append(row);
        });
      }
      // Express checkout would otherwise buy only the main variant.
      if (this.count > 1 && !this.checkoutState) {
        this.checkoutState = [...this.form.querySelectorAll('.product-form__dynamic-buttons, .shopify-payment-button')].map(e => [e, e.hidden]);
        this.checkoutState.forEach(([e]) => {e.hidden = true;});
      } else if (this.count === 1) this.restoreCheckout();
    }

    prepareItems(data) {
      if (this.count < 2) return;
      const properties = [...data.entries()].filter(([key]) => key.startsWith('properties['));
      ['id', 'quantity', ...properties.map(([key]) => key)].forEach(key => data.delete(key));
      const grouped = new Map();
      this.ids.slice(0, this.count).forEach(id => grouped.set(id, (grouped.get(id) || 0) + 1));
      [...grouped].forEach(([id, quantity], index) => {
        data.append(`items[${index}][id]`, String(id));
        data.append(`items[${index}][quantity]`, String(quantity));
        properties.forEach(([key, value]) => data.append(`items[${index}][properties][${key.slice(11, -1)}]`, value));
      });
    }

    schedule() {
      clearTimeout(this.delay);
      this.request?.abort();
      const sequence = ++this.sequence;
      this.cards.forEach((card, index) => {
        const amount = this.ids.slice(0, index + 1).reduce((sum, id) => sum + this.variant(id).price, 0);
        card.querySelector('[data-offer-price]').textContent = `${this.money.format(amount / 100)} total`;
      });
      this.status.textContent = 'Checking current price...';
      this.delay = setTimeout(() => this.quote(sequence), 350);
    }

    async quote(sequence) {
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const variables = {country: this.dataset.country || 'US'};
        const fields = [1, 2, 3].map(count => {
          variables[`input${count}`] = {
            buyerIdentity: {countryCode: variables.country},
            lines: this.ids.slice(0, count).map(id => ({merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: 1}))
          };
          return `offer${count}:cartCreate(input:$input${count}){cart{cost{subtotalAmount{amount currencyCode}}lines(first:3){nodes{quantity merchandise{... on ProductVariant{id}}}}}userErrors{message}}`;
        }).join(' ');
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST', credentials: 'omit', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({query: `mutation($country:CountryCode!,$input1:CartInput!,$input2:CartInput!,$input3:CartInput!) @inContext(country:$country){${fields}}`, variables}),
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Price unavailable');
        const result = await response.json();
        const prices = [1, 2, 3].map(count => {
          const offer = result.data?.[`offer${count}`];
          const amount = offer?.cart?.cost?.subtotalAmount;
          if (result.errors?.length || offer?.userErrors?.length || !amount || amount.currencyCode !== this.dataset.currency || !Number.isFinite(Number(amount.amount))) throw new Error('Price unavailable');
          const expected = this.ids.slice(0, count).map(String).sort();
          const actual = offer.cart.lines.nodes.flatMap(line => Array(line.quantity).fill(line.merchandise.id.split('/').pop())).sort();
          if (JSON.stringify(expected) !== JSON.stringify(actual)) throw new Error('Quantity unavailable');
          return Number(amount.amount);
        });
        if (sequence !== this.sequence || !this.isConnected) return;
        prices.forEach((price, index) => {this.cards[index].querySelector('[data-offer-price]').textContent = `${this.money.format(price)} total`;});
        this.status.textContent = `Estimated total (${this.count}): ${this.money.format(prices[this.count - 1])}`;
      } catch (error) {
        if (sequence === this.sequence && this.isConnected) this.status.textContent = 'Regular prices shown. Any eligible discount is calculated in your cart.';
      } finally { clearTimeout(timeout); }
    }
  }
  customElements.define('quantity-offer-controls', QuantityOfferControls);
})();
